// @ts-nocheck
import * as btc from '@scure/btc-signer'
import SafeLocalStorage from '../safe-local-storage'
import {
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_XVERSE_PAYMENT_ADDRESS,
	STORAGE_WALLET_TYPE,
	STORAGE_PUBLIC_KEY,
	STORAGE_XVERSE_TAPROOT_PUBLIC_KEY,
	NETWORK
} from '@/utils/constants'
import Wallet from './'
import { WalletProvider } from '@/utils/types/wallet'
import { sha256 } from 'js-sha256'
import { linkChildToSignedParent } from './chain'
import {
	allInputIndexes,
	dummyNestedSegwitPsbt,
	dummyTaprootPsbt,
	extractFinalizedPsbtHex,
	extractRawHex,
	finalizeInputs,
	finalizeTaprootKeyPath,
	inputScriptHex,
	inputTxidHex,
	parsePsbtBytes,
	parsePsbtTransaction,
	stripBip32Derivation,
	toPsbtBase64,
	toPsbtHex
} from './psbt'

/**
 * Output scripts this wallet controls for one of its addresses. Xverse hands
 * out the address and its public key; the payment address can be nested
 * SegWit (3…), native SegWit (bc1q…) or Taproot depending on the account, so
 * match the address itself first and only fall back to the historical
 * pubkey-derived shapes for stale sessions that stored no address.
 */
export function ownedScriptHexes(address: string | null, publicKey: string | null): Set<string> {
	const scripts = new Set<string>()
	if (address) {
		try {
			scripts.add(Buffer.from(btc.OutScript.encode(btc.Address().decode(address))).toString('hex'))
			// Xverse signs per address: an input paying another shape of the same
			// key is not signable through this address, so do not claim it.
			return scripts
		} catch {}
	}
	if (publicKey) {
		try {
			const pubkey = Buffer.from(publicKey, 'hex')
			scripts.add(Buffer.from(btc.p2sh(btc.p2wpkh(pubkey)).script).toString('hex'))
			scripts.add(Buffer.from(btc.p2wpkh(pubkey).script).toString('hex'))
			const xonly = pubkey.length === 33 ? pubkey.subarray(1) : pubkey
			scripts.add(Buffer.from(btc.p2tr(xonly).script).toString('hex'))
		} catch {}
	}
	return scripts
}

type InputToSign = { address: string; signingIndexes: number[]; sigHash?: number }

/** The one sats-connect payload shape every request uses. */
function signingPayload(psbtBase64: string, inputsToSign: InputToSign[]) {
	return {
		network: { type: 'Mainnet' },
		message: 'Sign Transaction',
		psbtBase64,
		broadcast: false,
		inputsToSign
	}
}

/** The API marks purchase dummy inputs with exactly this many sats. */
const DUMMY_INPUT_SATS = 600n

export default class XverseWallet implements WalletProvider {
	get name(): string {
		return 'Xverse'
	}

	get capabilities(): string[] {
		return ['payments-wallet']
	}

	get connectIcon(): string {
		return '/connect-xverse.png'
	}

	get enabled(): boolean {
		return NETWORK === 'BTC'
	}

	async connect(): Promise<boolean> {
		// @ts-ignore
		if (!window.BitcoinProvider) {
			window.open('https://www.xverse.app/download', '_blank')
			return false
		}

		await this.login()
		return true
	}

	async login(): Promise<void> {
		const { getAddress } = await import('sats-connect')

		return new Promise((res, rej) => {
			getAddress({
				payload: {
					purposes: ['ordinals', 'payment'],
					message: 'Address for receiving Ordinals and payments',
					network: {
						type: 'Mainnet'
					}
				},
				onFinish: (response) => {
					SafeLocalStorage.setItem(STORAGE_PUBLIC_KEY, response.addresses[1].publicKey)
					SafeLocalStorage.setItem(
						STORAGE_XVERSE_TAPROOT_PUBLIC_KEY,
						response.addresses[0].publicKey
					)
					SafeLocalStorage.setItem(STORAGE_TAPROOT_ADDRESS, response.addresses[0].address)
					SafeLocalStorage.setItem(STORAGE_XVERSE_PAYMENT_ADDRESS, response.addresses[1].address)
					SafeLocalStorage.setItem(STORAGE_WALLET_TYPE, 'xverse')
					Wallet.fetchProfile()

					res()
				},
				onCancel: () => rej()
			})
		})
	}

	balancePublicKey(): string {
		return SafeLocalStorage.getItem(STORAGE_PUBLIC_KEY)
	}
	balanceAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_XVERSE_PAYMENT_ADDRESS)
	}

	taprootPublicKey(): string {
		return SafeLocalStorage.getItem(STORAGE_XVERSE_TAPROOT_PUBLIC_KEY)
	}
	taprootAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	async dummyBalancePsbt(): Promise<string> {
		return dummyNestedSegwitPsbt(this.balancePublicKey())
	}

	async dummyPsbt(): Promise<string> {
		return dummyTaprootPsbt(this.taprootPublicKey())
	}

	/**
	 * One sats-connect signing round trip. Resolves with the wallet's PSBT
	 * parsed; the caller decides what to finalize and whether to extract.
	 */
	private async requestSignature(payload: ReturnType<typeof signingPayload>): Promise<btc.Transaction> {
		const { signTransaction } = await import('sats-connect')
		return new Promise((res, rej) => {
			signTransaction({
				payload,
				onFinish: (response) => {
					try {
						res(parsePsbtBytes(Buffer.from(response.psbtBase64, 'base64')))
					} catch (e) {
						rej(e)
					}
				},
				onCancel: () => rej('Request Cancelled'),
				onError: (e) => rej(e)
			})
		})
	}

	/** Sign every input with one address and return the fully finalized raw tx. */
	private async signAllAndExtract(psbt: string, address: string, sigHash?: number): Promise<string> {
		const tx = parsePsbtTransaction(psbt)
		const inputsToSign: InputToSign[] = [
			{ address, signingIndexes: allInputIndexes(tx), ...(sigHash !== undefined ? { sigHash } : {}) }
		]
		const signed = await this.requestSignature(signingPayload(toPsbtBase64(tx), inputsToSign))
		finalizeInputs(signed)
		return extractRawHex(signed)
	}

	/**
	 * Pair each input we own with the address that owns it, passing the
	 * PSBT's own sighash through. Inputs that are neither the payment nor the
	 * ordinals address (a seller's, an escrow's) are left for whoever owns them.
	 */
	async buildPSBTPayload(psbt: string): Promise<any> {
		const tx = parsePsbtTransaction(psbt)
		stripBip32Derivation(tx)

		const balanceScripts = ownedScriptHexes(this.balanceAddress(), this.balancePublicKey())
		const taprootScripts = ownedScriptHexes(this.taprootAddress(), this.taprootPublicKey())

		const inputsToSign: InputToSign[] = []
		const signingIndexes: number[] = []

		for (const i of allInputIndexes(tx)) {
			const script = inputScriptHex(tx, i) ?? ''
			const isPayment = balanceScripts.has(script)
			const isInscription = !isPayment && taprootScripts.has(script)
			if (!isPayment && !isInscription) continue
			signingIndexes.push(i)
			inputsToSign.push({
				address: isPayment ? this.balanceAddress() : this.taprootAddress(),
				signingIndexes: [i],
				sigHash: tx.getInput(i).sighashType
			})
		}

		return { ...signingPayload(toPsbtBase64(tx), inputsToSign), signingIndexes }
	}

	async signPSBTS(psbts: string[]): Promise<string[]> {
		const { signMultipleTransactions } = await import('sats-connect')

		// The later PSBTs spend the first one. Sign it alone first so every
		// child can be re-pointed at the txid the SIGNED setup actually has
		// (a nested-segwit payment input moves it at finalize).
		// signPSBT finalizes what it signs (and finalize strips the redeem
		// script), so the setup is extractable as-is; finalizing again fails.
		const setupRaw = extractFinalizedPsbtHex(await this.signPSBT(psbts[0]))

		const payloads = []
		for (let i = 0; i < psbts.length; i++) {
			let psbt = psbts[i]
			if (i > 0) {
				const stale = inputTxidHex(parsePsbtTransaction(psbt), 0)
				psbt = linkChildToSignedParent(setupRaw, psbt, [stale])
			}
			payloads.push(await this.buildPSBTPayload(psbt))
		}

		const payload = {
			network: { type: 'Mainnet' },
			message: 'Sign Transaction',
			psbts: payloads.map((e) => ({ psbtBase64: e.psbtBase64, inputsToSign: e.inputsToSign }))
		}

		return await new Promise((res, rej) => {
			signMultipleTransactions({
				payload,
				onFinish: (responses) => {
					try {
						const values = responses.map((response, i) => {
							const tx = parsePsbtBytes(Buffer.from(response.psbtBase64, 'base64'))
							finalizeInputs(tx, payloads[i].signingIndexes)
							return toPsbtHex(tx)
						})
						res(values)
					} catch (e) {
						rej(e)
					}
				},
				onCancel: () => rej('Request Cancelled')
			})
		})
	}

	async signPSBT(psbt: string): Promise<string> {
		const payload = await this.buildPSBTPayload(psbt)
		const tx = await this.requestSignature(payload)
		finalizeInputs(tx, payload.signingIndexes)
		return toPsbtHex(tx)
	}

	/** Commit for a mint: input 0 is the mint's own; the rest are ours to fund. */
	async signMintInscribe(psbt: string): Promise<string> {
		const tx = parsePsbtTransaction(psbt)
		const signingIndexes = allInputIndexes(tx).slice(1)
		const signed = await this.requestSignature(
			signingPayload(toPsbtBase64(tx), [{ address: this.balanceAddress(), signingIndexes }])
		)
		finalizeInputs(signed)
		return extractRawHex(signed)
	}

	async signInscribe(psbt: string): Promise<string> {
		return this.signAllAndExtract(psbt, this.balanceAddress())
	}

	async signPurchase(
		setup_psbt: string,
		purchase_psbt: string
	): Promise<{ signedSetup: string; signedPurchase: string }> {
		let signedSetup: string | undefined
		let purchase = purchase_psbt

		if (setup_psbt) {
			signedSetup = await this.signAllAndExtract(setup_psbt, this.balanceAddress())
			// Re-point the purchase at the setup's SIGNED txid and carry the
			// signed setup as its parent (chain.ts owns this; it used to be a
			// hand-rolled loop here, in Leather and in the native signer).
			const stale = inputTxidHex(parsePsbtTransaction(purchase_psbt), 0)
			purchase = linkChildToSignedParent(signedSetup, purchase_psbt, [stale])
		}

		const purchaseTx = parsePsbtTransaction(purchase)

		// The API funds a bulk purchase with N dummy inputs; only the first is
		// ours to sign, the rest are placeholders it finalizes itself.
		const dummyInputs = allInputIndexes(purchaseTx).filter(
			(i) => purchaseTx.getInput(i).witnessUtxo?.amount === DUMMY_INPUT_SATS
		).length
		const skipIndexes: number[] = []
		for (let i = 0; i < dummyInputs - 1; i++) {
			skipIndexes.push(i + dummyInputs)
		}

		const purchasePayload = await this.buildPSBTPayload(purchase)
		purchasePayload.inputsToSign = purchasePayload.inputsToSign.filter(
			(e) => !skipIndexes.includes(e.signingIndexes[0])
		)
		purchasePayload.signingIndexes = purchasePayload.signingIndexes.filter(
			(e) => !skipIndexes.includes(e)
		)

		// Every input that is not the seller's must be one Xverse will sign;
		// otherwise finalize throws "Not enough partial sign" after the user
		// has already approved the prompt. Fail before the prompt, and say why.
		const unsignable = allInputIndexes(purchaseTx).filter((i) => {
			const input = purchaseTx.getInput(i)
			return (
				!skipIndexes.includes(i) &&
				!purchasePayload.signingIndexes.includes(i) &&
				// scure parses an empty finalScriptSig onto every input; only
				// real bytes mean "already finalized".
				!(input.finalScriptWitness && input.finalScriptWitness.length > 0) &&
				!(input.finalScriptSig && input.finalScriptSig.length > 0)
			)
		})
		if (unsignable.length > 0) {
			throw new Error(
				`Purchase input${unsignable.length > 1 ? 's' : ''} ${unsignable.join(', ')} ` +
					`do not belong to the connected Xverse addresses (${this.balanceAddress()}, ` +
					`${this.taprootAddress()}). Reconnect the wallet and try again.`
			)
		}

		const signed = await this.requestSignature(purchasePayload)
		// Finalize only what Xverse signed; the seller's input arrives
		// finalized from the API and must not be touched.
		finalizeInputs(signed, purchasePayload.signingIndexes)
		const signedPurchase = toPsbtHex(signed)

		return { signedSetup, signedPurchase }
	}

	async signMessage(message: string): Promise<string> {
		const { signMessage: xVerseSignMessage } = await import('sats-connect')
		return new Promise((resolve, reject) => {
			const options = {
				payload: {
					network: {
						type: 'Mainnet'
					},
					address: this.taprootAddress(),
					message: message
				},
				onFinish: (signature) => {
					if (signature && message == 'Sign this message to login to Jpeg Wars.') {
						resolve(sha256(this.taprootAddress()))
					} else {
						resolve(signature)
					}
				},
				onCancel: () => {
					console.log('User declined request')
					reject(new Error('User declined request'))
				}
			}

			xVerseSignMessage(options).catch((error) => {
				console.log(`signature failed with error: ${error.message ?? 'unknown'}`)
				reject(error)
			})
		})
	}

	/** The first `inscriptions` inputs are ordinals (taproot); the rest pay the fee. */
	async signTransfer(psbt: string, inscriptions: number): Promise<string> {
		const tx = parsePsbtTransaction(psbt)
		const all = allInputIndexes(tx)
		const signed = await this.requestSignature(
			signingPayload(toPsbtBase64(tx), [
				{ address: this.taprootAddress(), signingIndexes: all.slice(0, inscriptions), sigHash: 1 },
				{ address: this.balanceAddress(), signingIndexes: all.slice(inscriptions), sigHash: 1 }
			])
		)
		finalizeInputs(signed)
		return extractRawHex(signed)
	}

	/** Listing: every input is the seller's taproot, signed SINGLE|ANYONECANPAY. */
	async signEscrow(psbt: string): Promise<string> {
		const tx = parsePsbtTransaction(psbt)
		const signingIndexes = allInputIndexes(tx)
		const signed = await this.requestSignature(
			signingPayload(toPsbtBase64(tx), [
				{ address: this.taprootAddress(), signingIndexes, sigHash: 131 }
			])
		)
		finalizeTaprootKeyPath(signed, signingIndexes)
		return toPsbtHex(signed)
	}
}
